import { useState, useEffect } from "react";
import Select from "react-select";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import "./AddCostModal.css";
import axiosInstance from "../../../Config/axiosInstance";
import { NumericFormat } from "react-number-format";

function EditCostModal({ open, onClose, costData, onUpdate }) {
  const [formData, setFormData] = useState({
    project_code: "",
    department: "",
    resource_type: "employer",
    resource: "",
    vat_payer: "yes",
    price_without_vat: "",
    vat: "21",
    price_with_vat: "",
    remarks: "",
    budget_type: "",
    expense_type: "",
    month: "",
    year: "",
    pay_date: "",
    registration_date: "",
    registration_date_display: "",
    document: null,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [originalVat, setOriginalVat] = useState("");

  //  Preîncarcă datele din costData
  useEffect(() => {
    if (costData) {
      const basePath = axiosInstance.defaults.baseURL?.replace(/\/$/, "") || "";

      const fileUrl = costData.document
        ? `${basePath}${costData.document}`
        : null;
      setOriginalVat(costData.vat || "");

      setFormData({
        project_code: costData.project_code || "",
        department: costData.department || "",
        resource_type: costData.supplier
          ? "supplier"
          : costData.resource
            ? "employer"
            : "department",
        resource:
          costData.supplier ||
          costData.resource ||
          costData.department_resource ||
          "",
        vat_payer: costData.vat_payer ? "yes" : "no",
        price_without_vat: costData.without_vat || "",
        vat: costData.vat || "",
        price_with_vat: costData.with_vat || "",
        remarks: costData.remarks || "",
        budget_type: costData.budget_type || "",
        expense_type: costData.expenses_type || "",
        month: costData.month || "",
        year: costData.year || "",
        pay_date: costData.pay_date || "",
        registration_date: costData.registration_date || "",
        registration_date_display: costData.registration_date
          ? new Date(costData.registration_date).toLocaleDateString("ro-RO")
          : "",
        document: null,
        existingDocument: fileUrl,
        user: costData.user
      });
    }
  }, [costData]);



  // Fetch dropdown data
  const [projectCodes, setProjectCodes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [budgetTypes, setBudgetTypes] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [monthChoices, setMonthChoices] = useState([]);
  const [yearChoices, setYearChoices] = useState([]);

  useEffect(() => {
    axiosInstance.get("/project-codes/").then((res) =>
      setProjectCodes(
        res.data.map((pc) => ({
          value: pc.id,
          label: `${pc.code} - ${pc.description}`,
        }))
      )
    );
    axiosInstance.get("/departments/").then((res) =>
      setDepartments(res.data.map((d) => ({ value: d.id, label: d.name })))
    );
    axiosInstance.get("/budget-type/").then((res) =>
      setBudgetTypes(
        res.data
          .filter((b) => b.status === "Active")
          .map((b) => ({ value: b.id, label: b.type }))
      )
    );
    axiosInstance.get("/expenses-type/").then((res) =>
      setExpenseTypes(
        res.data
          .filter((e) => e.status?.toLowerCase() === "active")
          .map((e) => ({ value: e.id, label: e.type }))
      )
    );
    axiosInstance.get("/suppliers/").then((res) =>
      setSuppliers(res.data.map((s) => ({ value: s.id, label: s.name })))
    );
    axiosInstance.get("/employees/").then((res) =>
      setEmployees(
        res.data.map((e) => ({
          value: e.id,
          label: `${e.first_name} ${e.last_name}`,
        }))
      )
    );
    axiosInstance.get("/month-choices/").then((res) => {
      const data = res.data;
      if (Array.isArray(data.month_choices)) {
        setMonthChoices(data.month_choices.map(([v, l]) => ({ value: v, label: l })));
      }
    });
    axiosInstance.get("/year-choices/").then((res) => {
      const data = res.data;
      if (Array.isArray(data.year_choices)) {
        setYearChoices(data.year_choices.map(([v, l]) => ({ value: v, label: l })));
      }
    });
  }, []);

  // Recalculează automat prețul cu TVA
  useEffect(() => {
    const withoutVat = parseFloat(formData.price_without_vat) || 0;
    const vatValue = parseFloat(formData.vat) || 0;
    const calculated = withoutVat + (withoutVat * vatValue) / 100;
    setFormData((prev) => ({ ...prev, price_with_vat: calculated.toFixed(2) }));
  }, [formData.price_without_vat, formData.vat]);

  // Handle Change
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };
  //  Când se schimbă VAT Payer — ajustează valoarea automat
  useEffect(() => {
    if (formData.vat_payer === "no") {
      setFormData((prev) => ({ ...prev, vat: "0" }));
    } else if (formData.vat_payer === "yes" && originalVat !== "") {
      setFormData((prev) => ({ ...prev, vat: originalVat.toString() }));
    }
  }, [formData.vat_payer, originalVat]);


  //  Validare
  const validateForm = () => {
    const newErrors = {};
    if (!formData.project_code) newErrors.project_code = "Project Code is required.";
    if (!formData.department) newErrors.department = "Department is required.";
    if (
      (formData.resource_type === "employer" && !formData.resource) ||
      (formData.resource_type === "supplier" && !formData.resource)
    ) {
      newErrors.resource = "Resource is required.";
    }

    if (!formData.price_without_vat)
      newErrors.price_without_vat = "Price without VAT is required.";
    if (formData.vat_payer === "yes" && (!formData.vat || formData.vat <= 0))
      newErrors.vat = "VAT is required when VAT Payer is 'Yes'.";
    if (!formData.remarks) newErrors.remarks = "Remarks are required.";
    if (!formData.budget_type) newErrors.budget_type = "Budget Type is required.";
    if (!formData.expense_type) newErrors.expense_type = "Expense Type is required.";
    if (!formData.month) newErrors.month = "Month is required.";
    if (!formData.year) newErrors.year = "Year is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  //  PUT Update
  const handleSave = async () => {
    if (!validateForm()) return;
    setLoading(true);

    try {
      const payload = {
        vat_payer: formData.vat_payer === "yes",
        without_vat: formData.price_without_vat?.toString() || "0.00",
        vat: parseFloat(formData.vat || 0),
        with_vat: formData.price_with_vat?.toString() || "0.00",
        remarks: formData.remarks || "",
        month: formData.month,
        year: parseInt(formData.year || new Date().getFullYear()),
        pay_date: formData.pay_date || null,
        registration_date:
          formData.registration_date || new Date().toISOString().split("T")[0],
        project_code: formData.project_code || null,
        department: formData.department || null,
        budget_type: formData.budget_type || null,
        expenses_type: formData.expense_type || null,
        user: formData.user ? parseInt(formData.user) : null,
      };

      // Logica salvare resource
      if (formData.resource_type === "supplier") {
        payload.supplier = formData.resource;
        payload.resource = null;
        payload.department_resource = null;
      } else if (formData.resource_type === "employer") {
        payload.resource = formData.resource;
        payload.supplier = null;
        payload.department_resource = null;
      } else if (formData.resource_type === "department") {
        payload.department_resource = formData.department;
        payload.supplier = null;
        payload.resource = null;
      }

      const formPayload = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        formPayload.append(k, v === null ? "" : v);
      });

      if (formData.document) {
        formPayload.append("document", formData.document);
      }

      const response = await axiosInstance.put(
        `/cost-administrative/${costData.id}/`,
        formPayload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (onUpdate) onUpdate(response.data);
      onClose();
    } catch (error) {
      console.error("❌ Eroare la actualizare:", error.response?.data || error);
      alert("Eroare la actualizare — verifică consola.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    if (costData) {
      setOriginalVat(costData.vat || "");
      setFormData({
        project_code: costData.project_code || "",
        department: costData.department || "",
        resource_type: costData.supplier
          ? "supplier"
          : costData.resource
            ? "employer"
            : "department",
        resource:
          costData.supplier ||
          costData.resource ||
          costData.department_resource ||
          "",
        vat_payer: costData.vat_payer ? "yes" : "no",
        price_without_vat: costData.without_vat || "",
        vat: costData.vat || "",
        price_with_vat: costData.with_vat || "",
        remarks: costData.remarks || "",
        budget_type: costData.budget_type || "",
        expense_type: costData.expenses_type || "",
        month: costData.month || "",
        year: costData.year || "",
        pay_date: costData.pay_date || "",
        registration_date: costData.registration_date || "",
        registration_date_display: costData.registration_date
          ? new Date(costData.registration_date).toLocaleDateString("ro-RO")
          : "",
        document: null,
      });
    }
  };


  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth className="add-cost-container">
      <DialogTitle className="title-popup" sx={{ position: "relative", paddingRight: "40px" }}>
        Edit Cost
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#555",
            "&:hover": { color: "black" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <div className="row">
          {/*  Project Code */}
          <div className="form-group small">
            <label className="label">
              Project Code <span className="required">*</span>
            </label>
            <Select
              name="project_code"
              options={projectCodes}
              placeholder="Select project code"
              isClearable
              classNamePrefix="react-select"
              className={errors.project_code ? "select-error" : ""}
              value={
                projectCodes.find((pc) => pc.value === formData.project_code) || null
              }
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  project_code: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, project_code: "" }));
              }}
            />
            {errors.project_code && (
              <p className="error-text">{errors.project_code}</p>
            )}
          </div>

          {/* Department */}
          <div className="form-group small">
            <label className="label">
              Department <span className="required">*</span>
            </label>
            <Select
              name="department"
              options={departments}
              placeholder="Select department"
              isClearable
              classNamePrefix="react-select"
              className={errors.department ? "select-error" : ""}
              value={
                departments.find((d) => d.value === formData.department) || null
              }
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  department: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, department: "" }));
              }}
            />
            {errors.department && (
              <p className="error-text">{errors.department}</p>
            )}
          </div>
        </div>

        {/* Resource Type */}
        <div className="form-group">
          <label className="label">
            Resource Type <span className="required">*</span>
          </label>
          <div className="radio-group">
            {["employer", "department", "supplier"].map((type) => (
              <label key={type}>
                <input
                  type="radio"
                  name="resource_type"
                  value={type}
                  checked={formData.resource_type === type}
                  onChange={(e) => {
                    const selectedType = e.target.value;

                    //Resetăm resource la fiecare schimbare de tip
                    setFormData((prev) => ({
                      ...prev,
                      resource_type: selectedType,
                      resource: "",
                    }));
                    setErrors((prev) => ({ ...prev, resource: "" }));
                  }}
                />{" "}
                {type === "employer"
                  ? "Employer"
                  : type === "department"
                    ? "Department Resource"
                    : "Supplier"}
              </label>
            ))}
          </div>
        </div>

        {/* Resource*/}
        <div className="form-group small">
          <label className="label">
            Resource <span className="required">*</span>
          </label>

          {formData.resource_type === "supplier" ? (
            <Select
              name="resource"
              options={suppliers}
              placeholder="Search supplier..."
              isClearable
              isSearchable
              classNamePrefix="react-select"
              className={errors.resource ? "select-error" : ""}
              value={suppliers.find((s) => s.value === formData.resource) || null}
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  resource: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, resource: "" }));
              }}
            />
          ) : formData.resource_type === "employer" ? (
            <Select
              name="resource"
              options={employees}
              placeholder="Select employee..."
              isClearable
              isSearchable
              classNamePrefix="react-select"
              className={errors.resource ? "select-error" : ""}
              value={employees.find((e) => e.value === formData.resource) || null}
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  resource: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, resource: "" }));
              }}
            />
          ) : (
            <input
              type="text"
              name="resource"
              className="input"
              readOnly
              value={
                departments.find((d) => d.value === formData.department)?.label || ""
              }
              style={{
                backgroundColor: "#eeeeee",
                cursor: "not-allowed",
              }}
              onChange={() => setErrors((prev) => ({ ...prev, resource: "" }))}
            />
          )}
          {errors.resource && <p className="error-text">{errors.resource}</p>}
        </div>



        {/*  VAT Payer */}
        <div className="form-group">
          <label className="label">VAT Payer</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="vat_payer"
                value="yes"
                checked={formData.vat_payer === "yes"}
                onChange={handleChange}
              />{" "}
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="vat_payer"
                value="no"
                checked={formData.vat_payer === "no"}
                onChange={handleChange}
              />{" "}
              No
            </label>
          </div>
        </div>

        {/* Prices without VAT*/}
        <div className="row">
          <div className="form-group small">
            <label className="label">
              Price without VAT <span className="required">*</span>
            </label>
            <NumericFormat
              name="price_without_vat"
              value={formData.price_without_vat}
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={2}
              fixedDecimalScale={false}
              allowNegative={false}
              customInput="input"
              className={`input ${errors.price_without_vat ? "input-error" : ""}`}
              onValueChange={(values) => {
                handleChange({
                  target: { name: "price_without_vat", value: values.floatValue ?? "" },
                });
                setErrors((prev) => ({ ...prev, price_without_vat: "" }));
              }}
            />

            {errors.price_without_vat && (
              <p className="error-text">{errors.price_without_vat}</p>
            )}
          </div>
          <div className="form-group small">
            <label className="label">
              VAT{" "}
              {formData.vat_payer === "yes" && (
                <span className="required">*</span>
              )}
            </label>

            <input
              type="number"
              name="vat"
              className={`input ${errors.vat && formData.vat_payer === "yes" ? "input-error" : ""
                }`}
              value={formData.vat}
              onChange={(e) => {
                handleChange(e);
                setErrors((prev) => ({ ...prev, vat: "" }));
              }}
              required={formData.vat_payer === "yes"}
              readOnly={formData.vat_payer === "no"}
              style={{
                backgroundColor:
                  formData.vat_payer === "no" ? "#eeeeee" : "white",
                cursor:
                  formData.vat_payer === "no" ? "not-allowed" : "auto",
              }}
            />
            {errors.vat && <p className="error-text">{errors.vat}</p>}
          </div>


          <div className="form-group small">
            <label className="label">Price with VAT</label>
            <NumericFormat
              name="price_with_vat"
              value={formData.price_with_vat}
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={2}
              fixedDecimalScale
              allowNegative={false}
              customInput="input"
              className="input"
              readOnly
              style={{
                backgroundColor: "#eeeeee",
                cursor: "not-allowed",
              }}
            />

          </div>
        </div>

        {/*Remarks  */}
        <div className="form-group">
          <label className="label">
            Remarks <span className="required">*</span>
          </label>
          <textarea
            name="remarks"
            className={`input textarea ${errors.remarks ? "input-error" : ""}`}
            value={formData.remarks}
            onChange={(e) => {
              handleChange(e);
              setErrors((prev) => ({ ...prev, remarks: "" }));
            }}
            placeholder="Add remarks..."
          />
          {errors.remarks && <p className="error-text">{errors.remarks}</p>}
        </div>

        <div className="row">
          {/*  Budget Type */}
          <div className="form-group small">
            <label className="label">
              Budget Type <span className="required">*</span>
            </label>
            <Select
              name="budget_type"
              options={budgetTypes}
              placeholder="Select budget type"
              classNamePrefix="react-select"
              className={errors.budget_type ? "select-error" : ""}
              value={
                budgetTypes.find((bt) => bt.value === formData.budget_type) || null
              }
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  budget_type: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, budget_type: "" }));
              }}
              menuPlacement="top"
              isClearable
            />
            {errors.budget_type && (
              <p className="error-text">{errors.budget_type}</p>
            )}
          </div>

          {/* Expense Type */}
          <div className="form-group small">
            <label className="label">
              Expense Type <span className="required">*</span>
            </label>
            <Select
              name="expense_type"
              options={expenseTypes}
              placeholder="Select expense type"
              classNamePrefix="react-select"
              className={errors.expense_type ? "select-error" : ""}
              value={
                expenseTypes.find((et) => et.value === formData.expense_type) || null
              }
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  expense_type: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, expense_type: "" }));
              }}
              menuPlacement="top"
              isClearable
            />
            {errors.expense_type && (
              <p className="error-text">{errors.expense_type}</p>
            )}
          </div>

          <div className="form-group small">
            <label className="label">Registration Date</label>
            <input
              type="text"
              name="registration_date_display"
              className="input"
              value={formData.registration_date_display}
              readOnly
              style={{
                backgroundColor: "#eeeeee",
                cursor: "not-allowed",
              }}
            />
          </div>
        </div>
        <div className="row">
          {/* Month */}
          <div className="form-group small">
            <label className="label">
              Month <span className="required">*</span>
            </label>
            <Select
              name="month"
              options={monthChoices}
              placeholder="Select month"
              classNamePrefix="react-select"
              className={errors.month ? "select-error" : ""}
              value={monthChoices.find((m) => m.value === formData.month) || null}
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  month: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, month: "" }));
              }}
              menuPlacement="top"
              isClearable
            />
            {errors.month && <p className="error-text">{errors.month}</p>}
          </div>

          {/*  Year */}
          <div className="form-group small">
            <label className="label">
              Year <span className="required">*</span>
            </label>
            <Select
              name="year"
              options={yearChoices}
              placeholder="Select year"
              classNamePrefix="react-select"
              className={errors.year ? "select-error" : ""}
              value={yearChoices.find((y) => y.value === formData.year) || null}
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  year: selectedOption ? selectedOption.value : "",
                });
                setErrors((prev) => ({ ...prev, year: "" }));
              }}
              menuPlacement="top"
              isClearable
            />
            {errors.year && <p className="error-text">{errors.year}</p>}
          </div>

          {/* Pay Date - CU ICONIȚĂ DE CALENDAR */}
          <div className="form-group small">
            <label className="label">Pay Date</label>
            <div className="datepicker-container-cost">
              <DatePicker
                selected={formData.pay_date ? new Date(formData.pay_date) : null}
                onChange={(date) => {
                  if (date) {
                    setFormData((prev) => ({
                      ...prev,
                      pay_date: format(date, "yyyy-MM-dd"),
                    }));
                  } else {
                    setFormData((prev) => ({ ...prev, pay_date: "" }));
                  }
                }}
                dateFormat="dd.MM.yyyy"
                placeholderText="dd.mm.yyyy"
                className="datepicker-input-cost"
                calendarStartDay={1}
                popperPlacement="bottom-start"
              />
              <span className="calendar-icon-cost">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </span>
            </div>
          </div>
        </div>

        {/*  Document */}
        <div className="form-group">
          <label className="label">Documents</label>
          <div className="upload-container">
            <input
              type="file"
              id="file-upload"
              name="document"
              onChange={handleChange}
              style={{ display: "none" }}
            />

            <Button
              variant="outlined"
              onClick={() => document.getElementById("file-upload").click()}
              startIcon={<CloudUploadIcon />}
              size="small"
              sx={{ textTransform: "none" }}
            >
              Choose File
            </Button>

            {/* Dacă există fișier NOU selectat */}
            {formData.document && (
              <div
                className="file-preview"
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span className="file-name" style={{ fontSize: "14px", color: "#333" }}>
                  {formData.document.name}
                </span>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Remove file"
                  onClick={() => setFormData((prev) => ({ ...prev, document: null }))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            )}

            {/* Dacă există fișier deja încărcat în înregistrare */}
            {!formData.document && formData.existingDocument && (
              <div
                className="file-preview"
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {(() => {
                  const fileUrl = formData.existingDocument;
                  const fileName = fileUrl.split("/").pop();
                  const fileExtension = fileName.split(".").pop().toLowerCase();

                  // Dacă este PDF -> deschide în browser
                  if (fileExtension === "pdf") {
                    return (
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-name"
                        style={{
                          fontSize: "14px",
                          color: "#1976d2",
                          textDecoration: "none",
                          maxWidth: "220px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title="Open PDF"
                      >
                        {fileName}
                      </a>
                    );
                  }

                  // Dacă NU e PDF -> descarcă automat
                  return (
                    <a
                      href={fileUrl}
                      download={fileName}
                      className="file-name"
                      style={{
                        fontSize: "14px",
                        color: "#1976d2",
                        textDecoration: "none",
                        maxWidth: "220px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title="Download file"
                    >
                      {fileName}
                    </a>
                  );
                })()}

                <IconButton
                  size="small"
                  color="error"
                  aria-label="Remove existing file"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, existingDocument: null }))
                  }
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            )}

          </div>
        </div>

      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} className="button discard">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} className="button save" disabled={loading}>
          {loading ? "Updating..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditCostModal;